import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import { ResponseStatus } from "@/domain/entities/agenda/AgendaItemResponseStatus";
import Language from "@/domain/enums/Language";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import useGetDateFnsLocale from "@/hooks/utils/useGetDateFnsLocale";
import i18n from "@/i18n";
import sanitizeHTML from "@/lib/sanitizeHTML";
import { Clock, MapPin, NotebookPen, User, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import getFormattedDateTime from "../../getFormattedDateTime";
import EventAttendeeList from "./EventAttendeeList";

type EventDetailsProps = {
    event: AgendaItem;
};

const EventDetails = ({ event }: EventDetailsProps) => {
    const { t } = useTranslation();
    const { data: user } = useGetUserProfile();

    const locale = useGetDateFnsLocale(i18n.language as Language);

    const dateTime = getFormattedDateTime(event, locale);

    if (
        !user ||
        (user && event.getUserAcceptedStatus(user) === ResponseStatus.Declined)
    ) {
        return null;
    }

    return (
        <div className="max-h-[500px]">
            {event.onlineMeeting?.joinUrl && (
                <a
                    className="meeting-link-button mb-4 flex w-fit items-center gap-2 rounded-md border border-white bg-white px-2 py-1 pr-2.5 text-sm !text-primary-dark !no-underline opacity-90 shadow-sm transition-all duration-200 hover:opacity-100"
                    href={event.onlineMeeting?.joinUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    <Video className="h-4 w-4" />
                    {t("dashboard.widgets.calendar.event.join")}
                </a>
            )}

            <div className="mb-2 flex flex-col gap-1.5">
                <p className="flex items-center gap-4 text-sm capitalize">
                    <Clock className="mt-[-2px] h-4 w-4" />
                    {dateTime}
                </p>
                <hr className="opacity-50" />
                {event.location?.displayName && (
                    <>
                        <p className="flex items-center gap-4 text-sm">
                            <MapPin className="mt-[-2px] h-4 w-4" />
                            {event.location.displayName.startsWith(
                                "https://",
                            ) ? (
                                <a
                                    className="underline"
                                    href={event.location.displayName}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    {event.location.displayName}
                                </a>
                            ) : (
                                event.location.displayName
                            )}
                        </p>
                        <hr className="opacity-50" />
                    </>
                )}
                {(event.body || event.bodyPreview) && (
                    <>
                        <div className="flex items-start gap-4">
                            <NotebookPen className="h-4 w-4" />
                            <div className="w-full text-sm [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4">
                                {/* eslint-disable-next-line react/no-danger */}
                                {event.body && (
                                    <div
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeHTML(event.body),
                                        }}
                                        className="max-w-full overflow-wrap-anywhere"
                                    />
                                )}
                                {!event.body && event.bodyPreview && (
                                    <p className="overflow-wrap-anywhere">
                                        {event.bodyPreview}
                                    </p>
                                )}
                            </div>
                        </div>
                        <hr className="opacity-50" />
                    </>
                )}
            </div>
            <div className="flex flex-col gap-1">
                <p className="flex items-center gap-4 text-sm capitalize">
                    <User className="mt-[-4px] h-4 w-4" />
                    {t("dashboard.widgets.calendar.event.organizer")}:{" "}
                    {event.organizer?.emailAddress?.name}
                </p>
                {event.attendees?.length > 0 && (
                    <EventAttendeeList
                        attendees={event.attendees}
                        organizer={event.organizer}
                    />
                )}
            </div>
        </div>
    );
};

export default EventDetails;
